import React, { useEffect, useMemo, useState } from "react";
import { Button, Stack } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PageHeader } from "@/CommonElements/PageTitle";
import { Controllers } from "@/CommonElements/Controller";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { AntInput } from "@/CommonElements/AntInput";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useSystem } from "@/utils/hooks/useLocation";
import {
  useGetOneNusukCardsBatchQuery,
  useUpdateNusukCardsBatchMutation,
} from "@/services/nusukCards/nusukCards";
import { handleFormErrors } from "@/utils/helpers";

const COL_LAYOUT = { colProps: { xs: 12, md: 4 } };
const COL_LAYOUT_HALF = { colProps: { xs: 12, md: 6 } };

function deriveBatchApiContext(row) {
  if (!row) return { entity: "campaign", campaignId: null };
  const entityRaw = row.entity ?? row.entity_type;
  let entity = "campaign";
  if (entityRaw === "group" || entityRaw === "campaign") {
    entity = entityRaw;
  } else if (row.group_id != null && row.campaign_id == null) {
    entity = "group";
  }
  const campaignId =
    Number(row.campaign_id) ||
    Number(row.group_id) ||
    Number(row.entity_details?.campaign_id) ||
    Number(row.entity_details?.id) ||
    null;
  return { entity, campaignId };
}

const EditNusukCardsBatch = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  const { getLocalizedText } = useLocalizedText();
  const backLink = `/app/${currentSystem}/nusuk-cards`;

  const { data, isLoading, isError } = useGetOneNusukCardsBatchQuery(id);
  const row = data?.data?.data ?? data?.data ?? null;

  const { mutate: updateBatch, isPending } = useUpdateNusukCardsBatchMutation();
  const [apiContext, setApiContext] = useState({ entity: "campaign", campaignId: null });

  const form = useForm({
    disabled: isPending,
    defaultValues: {
      batch_no: "",
      group_name: "",
      card_no: "",
      representative_name: "",
      safe_number: "",
      notes: "",
    },
  });
  const { control, reset, handleSubmit, watch, formState: { errors } } = form;

  useEffect(() => {
    if (!row) return;
    const ctx = deriveBatchApiContext(row);
    setApiContext(ctx);
    reset({
      batch_no: row.batch_no ?? "",
      group_name: row.group_name ?? "",
      card_no: row.card_no != null ? String(row.card_no) : "",
      representative_name: row.representative_name ?? "",
      safe_number: row.safe_number != null ? String(row.safe_number) : "",
      notes: row.notes ?? "",
    });
  }, [row, reset]);

  const delivered = Number(row?.delivery_status) === 1;
  const canSubmit =
    !delivered &&
    !isPending &&
    id &&
    apiContext.campaignId != null &&
    !Number.isNaN(apiContext.campaignId);

  const onSubmit = (data) => {
    if (!id) {
      toast.error(t("SOMETHING_WENT_WRONG"));
      return;
    }
    if (!canSubmit) {
      if (delivered) toast.warning(t("NUSUK_BATCH_NOT_EDITABLE_DELIVERED"));
      else toast.error(t("SOMETHING_WENT_WRONG"));
      return;
    }

    const payload = {
      batch_no: data.batch_no,
      entity: apiContext.entity,
      campaign_id: apiContext.campaignId,
      group_name: data.group_name,
      card_no: data.card_no,
      representative_name: data.representative_name,
      safe_number: data.safe_number,
      notes: data.notes?.trim() ? data.notes.trim() : null,
      system: currentSystem,
    };

    updateBatch(
      { batchId: id, payload },
      {
        onSuccess: () => {
          toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
          navigate(backLink);
        },
        onError: (error) => {
          const handled = handleFormErrors(
            error,
            form.setError,
            t,
            "SOMETHING_WENT_WRONG"
          );
          if (!handled) {
            toast.error(error?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
          }
        },
      }
    );
  };

  const formControllers = useMemo(() => {
    const ctrls = [
      {
        labelProps: { children: t("NUSUK_BATCH_NUMBER"), className: "fw-bold required" },
        controllerProps: {
          name: "batch_no",
          rules: { required: t("FIELD_REQUIRED") },
          render: ({ field }) => (
            <AntInput
              {...field}
              disabled={delivered || isPending}
              status={errors.batch_no ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("GROUP_NAME"), className: "fw-bold required" },
        controllerProps: {
          name: "group_name",
          rules: { required: t("FIELD_REQUIRED") },
          render: ({ field }) => (
            <AntInput
              {...field}
              disabled={delivered || isPending}
              status={errors.group_name ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("NUSUK_CARDS_COUNT"), className: "fw-bold required" },
        controllerProps: {
          name: "card_no",
          rules: {
            required: t("FIELD_REQUIRED"),
            validate: (value) => {
              if (!value) return true;
              if (!/^\d+$/.test(String(value)))
                return t("MUST_BE_NUMBERS_DIGITS", { defaultValue: "Must be numbers only" });
              if (String(value).length > 3) return t("NUSUK_MAX_DIGITS", { max: 3 });
              return true;
            },
          },
          render: ({ field }) => (
            <AntInput
              {...field}
              maxLength={3}
              disabled={delivered || isPending}
              status={errors.card_no ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("NUSUK_REPRESENTATIVE_NAME"), className: "fw-bold required" },
        controllerProps: {
          name: "representative_name",
          rules: {
            required: t("FIELD_REQUIRED"),
            maxLength: { value: 30, message: t("NUSUK_MAX_CHARS", { max: 30 }) },
          },
          render: ({ field }) => (
            <AntInput
              {...field}
              maxLength={30}
              disabled={delivered || isPending}
              status={errors.representative_name ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("NUSUK_SAFE_NUMBER"), className: "fw-bold" },
        controllerProps: {
          name: "safe_number",
          render: ({ field }) => (
            <AntInput
              {...field}
              placeholder={t("NUSUK_SAFE_NUMBER")}
              disabled={delivered || isPending}
              status={errors.safe_number ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("NOTES"), className: "fw-bold" },
        controllerProps: {
          name: "notes",
          render: ({ field }) => (
            <AntInput
              {...field}
              placeholder={t("NOTES")}
              disabled={delivered || isPending}
              status={errors.notes ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT_HALF,
      },
    ];

    return ctrls.map((c) => ({
      ...c,
      controllerProps: { ...c.controllerProps, control, errors },
    }));
  }, [t, control, errors, delivered, isPending]);

  if (isLoading) {
    return (
      <Stack gap={2}>
        <PageHeader title={t("NUSUK_EDIT_BATCH_SCREEN")} backTo={backLink} sticky={false} />
        <Container fluid className="d-flex justify-content-center py-5">
          <Spinner />
        </Container>
      </Stack>
    );
  }

  if (isError || !row) {
    return (
      <Stack gap={2}>
        <PageHeader title={t("NUSUK_EDIT_BATCH_SCREEN")} backTo={backLink} sticky={false} />
        <Container fluid>
          <p className="text-muted mb-3">{t("NO_DATA")}</p>
          <Link to={backLink} className="btn btn-primary">
            {t("BACK")}
          </Link>
        </Container>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <PageHeader title={t("NUSUK_EDIT_BATCH_SCREEN")} backTo={backLink} sticky={false} />

      <Container fluid>
        {delivered && (
          <div className="alert alert-warning mb-3" role="alert">
            {t("NUSUK_BATCH_NOT_EDITABLE_DELIVERED")}
          </div>
        )}

        <div className="card border rounded-3 mb-4">
          <div className="card-body py-3 px-4">
            <Row>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("CAMPAIGN_OR_GROUP_NUMBER")}
                  value={row?.entity_details?.number ?? row?.campaign_id ?? "-"}
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("SERVICE_CENTER")}
                  value={
                    getLocalizedText(
                      row?.entity_details?.service_center?.name_ar,
                      row?.entity_details?.service_center?.name_en
                    ) ||
                    row?.entity_details?.service_center?.name ||
                    "-"
                  }
                  direction="vertical"
                />
              </Col>
              <Col xs={12} md={6} lg={4} className="mb-3">
                <LabelValue
                  label={t("NATIONALITY")}
                  value={
                    getLocalizedText(
                      row?.entity_details?.nationality?.name_ar,
                      row?.entity_details?.nationality?.name_en
                    ) ||
                    row?.entity_details?.nationality?.name ||
                    "-"
                  }
                  direction="vertical"
                />
              </Col>
            </Row>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="card border rounded-3 mb-4 py-3 px-4">
            <Controllers controllers={formControllers} watch={watch} />
          </div>

          <ActionsFooter>
            <Button
              className="btn btn-primary"
              type="submit"
              disabled={!canSubmit}
            >
              {isPending ? (
                <Spinner animation="border" size="sm" className="me-2" role="status" />
              ) : null}
              {t("SAVE")}
            </Button>
            <Link
              to={backLink}
              className="btn btn-danger"
              style={{ pointerEvents: isPending ? "none" : "auto" }}
            >
              {t("CANCEL")}
            </Link>
          </ActionsFooter>
        </form>
      </Container>
    </Stack>
  );
};

export default EditNusukCardsBatch;
